import { fetchThunk } from '@spike/appointments-list-action';
import { AppointmentListItem } from '@spike/appointments-list-model';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClockRotateLeft } from '@fortawesome/pro-regular-svg-icons';
import { FunctionComponent, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import useNonInitialEffect from '@versiondos/hooks';
import { useApiClientWrapper } from 'hooks';
import useOrganizationMarketplaces from 'hooks/useOrganizationMarketplaces';
import useTimeZone from 'hooks/useTimeZone';
import { RootState } from 'store';
import { allAllTab } from 'components/Bookings2/List/tabs';
import { getBackendTabsByTabId, getStatusIdsByTabId } from 'components/Bookings2/List/tabs';
import { SortOrder } from '@spike/appointments-list-model';
import { bookedAtSorter } from 'components/Bookings2/List/sorting';
import { AppointmentsListStatus } from '@spike/appointments-list-action';
import styles from './RecentlyBooked.module.scss';
import Spinner from 'components/UI/V3/Spinner';
import { status as appointmentStatus } from '@spike/appointment-model';
import moment from 'moment-timezone';

export interface RecentlyBookedProps {
    clientId: number;
    petId?: number;
    refreshTrigger?: number;
    onViewAll?: () => void;
    onAppointmentClick?: (appointment: AppointmentListItem) => void;
}

export const RecentlyBooked: FunctionComponent<RecentlyBookedProps> = ({
    clientId,
    petId,
    refreshTrigger,
    onViewAll,
    onAppointmentClick
}) => {
    const dispatch = useDispatch();
    const apiClientWrapper = useApiClientWrapper();
    const { organizationMarketplaces } = useOrganizationMarketplaces();
    const timeZone = useTimeZone();

    const list = useSelector<RootState, Array<AppointmentListItem>>(state => state.appointmentsList.all.list);
    const status = useSelector<RootState, AppointmentsListStatus>(state => state.appointmentsList.status);
    const [isLoading, setIsLoading] = useState(false);

    useNonInitialEffect(() => {
        switch (status) {
            case AppointmentsListStatus.AllFetching:
                setIsLoading(true);
                break;
            case AppointmentsListStatus.AllFetchSuccess:
            case AppointmentsListStatus.AllError:
                setIsLoading(false);
                break;
        }
    }, [status]);

    const mostRecent = useMemo(() => {
        if (!list?.length) return null;
        const nowInTz = moment.tz(timeZone);
        const upToToday = list.filter(app => app.startAt.clone().tz(timeZone).isSameOrBefore(nowInTz, 'day'));
        if (!upToToday.length) return null;
        const sorted = bookedAtSorter.sort([...upToToday], SortOrder.DESCENDING);
        return sorted[0] ?? null;
    }, [list, timeZone]);

    const fetchAppointments = useCallback(() => {
        if (!clientId) return;
        const selectedMarketplaceIds = organizationMarketplaces.map(m => m.id);
        dispatch(
            fetchThunk(
                apiClientWrapper,
                'all',
                'all',
                selectedMarketplaceIds,
                allAllTab.id,
                null,
                null,
                null,
                [
                    appointmentStatus.BOOKED,
                    appointmentStatus.CONFIRMED,
                    appointmentStatus.CHECKED_IN,
                    appointmentStatus.IN_PROGRESS,
                    appointmentStatus.READY_FOR_CHECK_OUT,
                    appointmentStatus.PAYMENT,
                    appointmentStatus.WAITING_PAYMENT_CONFIRMATION,
                    appointmentStatus.COMPLETED
                ],
                [],
                getBackendTabsByTabId(allAllTab.id),
                [],
                clientId,
                petId ?? null
            )
        );
    }, [clientId, petId, dispatch, apiClientWrapper, organizationMarketplaces]);

    useEffect(() => {
        fetchAppointments();
    }, [clientId, petId, refreshTrigger]);

    return (
        <div className={styles.container}>
            {!isLoading && mostRecent && (
                <div className={styles.header}>
                    <label className={styles.title}>Recently Booked</label>
                    <div
                        className={styles.viewAllButton}
                        onClick={() => onViewAll?.()}
                    >
                        <FontAwesomeIcon
                            icon={faClockRotateLeft}
                            className={styles.viewAllIcon}
                        />
                        <span className={styles.viewAllText}>View all</span>
                    </div>
                </div>
            )}

            {isLoading ? (
                <Spinner />
            ) : mostRecent ? (
                <button
                    type="button"
                    className={styles.card}
                    onClick={() => onAppointmentClick?.(mostRecent)}
                >
                    <div className={styles.cardLeft}>
                        <div className={styles.serviceName}>{mostRecent.service.name}</div>
                        <div className={styles.meta}>
                            {mostRecent.startAt.format('MMM D, YYYY')}
                            <span className={styles.metaSeparator}> · </span>
                            {mostRecent.staff?.name ?? 'Unassigned'}
                        </div>
                    </div>
                    <div className={styles.price}>${Number(mostRecent.total).toFixed(2)}</div>
                </button>
            ) : (
                <div className={styles.emptyPlaceholder}>This pet has no booking history.</div>
            )}
        </div>
    );
};

export default RecentlyBooked;
